import React from 'react';
import { Link } from 'react-router-dom';
import { Siren, MessageSquareText, LineChart } from 'lucide-react';
import { clsx } from 'clsx';
import type { PulseAnomalyCard, PulseSeverity } from '../../services/pulse-api';
import { useAuth } from '../../contexts/AuthContext';

interface AnomalyCardProps {
  card: PulseAnomalyCard;
  /** Show the account name above the headline (used on the cross-account feed). */
  showAccount?: boolean;
}

/**
 * Renders an anomaly. Investigated cards get a coloured severity rail, the AI
 * impact, and a chat-thread link. Minor (gated) cards are compact: the same
 * headline + signal, but with a "Below alert threshold" hint.
 */
export const AnomalyCard: React.FC<AnomalyCardProps> = ({ card, showAccount = false }) => {
  if (card.investigated) return <InvestigatedCard card={card} showAccount={showAccount} />;
  return <MinorCard card={card} showAccount={showAccount} />;
};

const InvestigatedCard: React.FC<AnomalyCardProps> = ({ card, showAccount }) => {
  const railColor = severityRailClass(card.severity);
  const badge = severityBadgeClass(card.severity);

  return (
    <div className="bg-white dark:bg-white/5 border border-border rounded-xl overflow-hidden">
      <div className={clsx('h-1', railColor)} />
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className={clsx('inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded', badge)}>
              {card.severity === 'ALERT!' && <Siren className="w-3 h-3" />}
              {card.severity}
            </span>
            {showAccount && <AccountTag card={card} />}
            <span className="text-sm text-muted-foreground truncate">{card.signalLabel}</span>
          </div>
          {card.impact !== null && (
            <div className="text-right shrink-0">
              <div className="text-xs text-muted-foreground">{card.impactIsEstimate ? 'Est. impact' : 'Estimated impact'}</div>
              <div className={clsx('text-lg font-bold leading-tight', severityImpactClass(card.severity))}>
                {formatCurrency(card.impact)}
              </div>
            </div>
          )}
        </div>

        <p className="text-[15px] text-foreground mt-3 leading-snug">{card.headline}</p>

        {card.natureChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {card.natureChips.slice(0, 2).map((chip, i) => (
              <span
                key={i}
                className="text-[11px] font-medium text-muted-foreground bg-muted px-2 py-1 rounded"
              >
                {chip}
              </span>
            ))}
            {card.natureChips.length > 2 && (
              <span className="text-[11px] font-medium text-muted-foreground bg-muted px-2 py-1 rounded">
                +{card.natureChips.length - 2} more
              </span>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-border text-sm">
          <Meta label="Detected" value={formatDateTime(card.detectedAt)} />
          <Meta label="Last transaction" value={formatDateTime(card.lastTransactionAt)} />
          <Meta label="Last sync" value={formatDateTime(card.lastSyncAt)} />
        </div>

        <div className="flex items-center gap-4 mt-4">
          <Link
            to={`/dynamic-analysis/results/${card.resultId}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <LineChart className="w-4 h-4" /> View analysis
          </Link>
          {card.threadUrl && (
            <Link
              to={card.threadUrl}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              <MessageSquareText className="w-4 h-4" /> Open investigation
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const MinorCard: React.FC<AnomalyCardProps> = ({ card, showAccount }) => (
  <Link
    to={`/dynamic-analysis/results/${card.resultId}`}
    className="block bg-white dark:bg-white/5 border border-border rounded-xl p-4 hover:border-primary/40 transition-colors"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-bold text-warn bg-warn/15 px-2 py-0.5 rounded">
            {card.severity}
          </span>
          {showAccount && <AccountTag card={card} />}
          <span className="text-sm text-muted-foreground truncate">{card.signalLabel}</span>
        </div>
        <p className="text-sm text-foreground mt-1.5">{card.headline}</p>
        <div className="text-xs text-muted-foreground mt-1.5">
          {formatDateTime(card.detectedAt)} · Below alert threshold — not investigated
        </div>
      </div>
      {card.impact !== null && (
        <div className="text-right shrink-0">
          <div className="text-xs text-muted-foreground">Est. impact</div>
          <div className="text-sm font-semibold text-muted-foreground">{formatCurrency(card.impact)}</div>
        </div>
      )}
    </div>
  </Link>
);

/**
 * Which account (and, in a multi-business workspace, which business) an anomaly
 * belongs to — the feed spans the whole workspace (R11), so the business is what
 * disambiguates two same-named registers.
 */
const AccountTag: React.FC<{ card: PulseAnomalyCard }> = ({ card }) => {
  const { organizations } = useAuth();
  return (
    <>
      {organizations.length > 1 && (
        <>
          <span className="text-xs font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate">
            {card.organizationName}
          </span>
          <span className="text-muted-foreground/60">·</span>
        </>
      )}
      <span className="text-sm font-medium text-foreground truncate">{card.accountName}</span>
      <span className="text-muted-foreground/60">·</span>
    </>
  );
};

const Meta: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-muted-foreground text-xs">{label}</div>
    <div className="mt-0.5 text-foreground">{value}</div>
  </div>
);

// ---------------------------------------------------------------------------

function severityRailClass(severity: PulseSeverity): string {
  if (severity === 'ALERT!') return 'bg-destructive';
  if (severity === 'Dangerous') return 'bg-alert';
  return 'bg-warn';
}

function severityBadgeClass(severity: PulseSeverity): string {
  if (severity === 'ALERT!') return 'text-destructive bg-destructive/15';
  if (severity === 'Dangerous') return 'text-alert bg-alert/15';
  return 'text-warn bg-warn/15';
}

function severityImpactClass(severity: PulseSeverity): string {
  if (severity === 'ALERT!') return 'text-destructive';
  if (severity === 'Dangerous') return 'text-alert';
  return 'text-warn';
}

function formatCurrency(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
